import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';
import { cn } from 'lib/utils';

interface Props {
  fileName: string;
  fileSize: number;
  aiOn: boolean;
}

const TICK_MS = 1300;
const DOC_GROUPS = [
  ['82%', '64%', '48%', '58%'],
  ['70%', '52%', '66%', '40%'],
  ['88%', '44%', '60%', '54%'],
  ['62%', '58%', '42%', '70%'],
];

const formatSize = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * Shown while the server reads the file. The read is one request with no
 * progress events, so nothing here is a measurement: the steps advance on a
 * timer and hold one short of the end until the response replaces this panel.
 * No percentage and no question count, because there is nothing true to show.
 */
export const ImportProcessingPanel: React.FC<Props> = ({ fileName, fileSize, aiOn }) => {
  const { t } = useTranslation();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((value) => value + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const steps = [
    t('import_step_read'),
    t('import_step_split'),
    ...(aiOn ? [t('import_step_ai')] : []),
    t('import_step_check'),
  ];
  const activeIndex = Math.min(tick, steps.length - 2);
  const extension = fileName.includes('.') ? (fileName.split('.').pop() ?? '').slice(0, 4).toUpperCase() : '';

  return (
    <div className="space-y-4" role="status" aria-live="polite">
      <div className="relative overflow-hidden rounded-xl border border-border p-4 pb-5">
        <div className="flex items-center gap-3.5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-[10.5px] font-extrabold tracking-wide text-muted-foreground">
            {extension}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{fileName}</p>
            {fileSize > 0 && <p className="text-xs text-muted-foreground">{formatSize(fileSize)}</p>}
          </div>
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
        <div className="absolute inset-x-0 bottom-0 h-1 overflow-hidden bg-muted">
          <div className="absolute inset-y-0 left-0 w-1/3 animate-import-slide rounded-full bg-primary motion-reduce:animate-none" />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
        <div aria-hidden="true" className="relative flex flex-col gap-2.5 overflow-hidden rounded-xl border border-border bg-muted/40 p-3.5">
          {DOC_GROUPS.map((widths, index) => (
            <div
              key={index}
              className={cn(
                'flex flex-col gap-1.5 rounded-lg border p-2.5 transition-colors duration-500',
                index <= tick ? 'border-primary bg-primary/15' : 'border-border bg-card',
              )}
            >
              <span className="h-2 rounded bg-muted-foreground/40" style={{ width: widths[0] }} />
              {widths.slice(1).map((width, row) => (
                <span key={row} className="flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/30" />
                  <span className="h-1.5 rounded bg-muted-foreground/25" style={{ width }} />
                </span>
              ))}
            </div>
          ))}
          <div className="pointer-events-none absolute inset-0 animate-import-scan border-b-2 border-primary bg-gradient-to-t from-primary/30 to-transparent motion-reduce:hidden" />
        </div>

        <ol className="flex flex-col justify-center gap-3.5">
          {steps.map((label, index) => {
            const done = index < activeIndex;
            const active = index === activeIndex;
            return (
              <li key={label} className="flex items-center gap-3">
                {done ? (
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                ) : active ? (
                  <span className="h-6 w-6 shrink-0 animate-spin rounded-full border-[2.5px] border-border border-t-foreground" />
                ) : (
                  <span className="h-6 w-6 shrink-0 rounded-full border-2 border-border" />
                )}
                <span
                  className={cn(
                    'text-sm',
                    done || active ? 'font-semibold text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      <p className="text-center text-xs text-muted-foreground">{aiOn ? t('import_ai_fixing') : t('reading_file')}</p>
    </div>
  );
};

export default ImportProcessingPanel;
